/**
 * HowItWorksStep - Second step of the onboarding flow
 *
 * Explains the Paste -> Analyze -> Improve loop.
 * Visual step indicators show the three-phase workflow.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

export const HowItWorksStep: React.FC = () => {
  const { t } = useTranslation('common');

  const STEPS = [
    {
      number: '1',
      icon: '📋',
      title: t('how_step_paste_title', { defaultValue: 'Paste' }),
      description: t('how_step_paste_desc', { defaultValue: 'Copy any conversation — texts, emails, DMs, or group chats.' }),
    },
    {
      number: '2',
      icon: '🔬',
      title: t('how_step_analyze_title', { defaultValue: 'Analyze' }),
      description: t('how_step_analyze_desc', { defaultValue: 'See how your message lands — before you hit send.' }),
    },
    {
      number: '3',
      icon: '🎯',
      title: t('how_step_improve_title', { defaultValue: 'Improve' }),
      description: t('how_step_improve_desc', { defaultValue: 'Get rewritten messages and actionable advice to communicate healthier.' }),
    },
  ];

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('how_it_works_title', { defaultValue: 'How It Works' })}</Text>
      <Text style={styles.subtitle}>{t('how_it_works_subtitle', { defaultValue: 'Three simple steps to better communication' })}</Text>
      <View style={styles.stepsContainer}>
        {STEPS.map((step, index) => (
          <View key={step.number} style={styles.stepRow}>
            <View style={styles.stepIndicator}>
              <View style={styles.stepCircle}>
                <Text style={styles.stepIcon}>{step.icon}</Text>
              </View>
              {index < STEPS.length - 1 && <View style={styles.connector} />}
            </View>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>{step.title}</Text>
              <Text style={styles.stepDescription}>{step.description}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1a1a2e',
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 32,
    fontFamily: 'Inter_400Regular',
  },
  stepsContainer: {
    width: '100%',
    gap: 4,
  },
  stepRow: {
    flexDirection: 'row',
    gap: 16,
  },
  stepIndicator: {
    alignItems: 'center',
    width: 48,
  },
  stepCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(168, 85, 247, 0.1)',
    borderWidth: 2,
    borderColor: 'rgba(168, 85, 247, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepIcon: {
    fontSize: 22,
  },
  connector: {
    width: 2,
    height: 32,
    backgroundColor: 'rgba(168, 85, 247, 0.2)',
    marginVertical: 4,
  },
  stepContent: {
    flex: 1,
    paddingTop: 4,
    paddingBottom: 16,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: 4,
    fontFamily: 'Inter_600SemiBold',
  },
  stepDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    fontFamily: 'Inter_400Regular',
  },
});
